import { isEqual } from "lodash";
import {
    AgentDetailContractSubScope,
    AgentDetailSavePayload,
    AgentDetailSaveScope,
    AgentDetailSection,
    IAgentDetails,
    IAgentRestrictedFields,
} from "../../agentDetail.model";

export const cloneAgentDetails = (details: IAgentDetails): IAgentDetails =>
    structuredClone(details);

export const normalizeDraftFieldValue = (value: unknown): unknown =>
    value === "" ? null : value;

export const isFieldEditable = (
    restricted: IAgentRestrictedFields,
    section: AgentDetailSection,
    fieldKey: string
): boolean => !(restricted[section]?.includes(fieldKey) ?? false);

const isContractSubScope = (scope: AgentDetailSaveScope): scope is AgentDetailContractSubScope =>
    typeof scope === "object" && scope !== null;

export const buildAgentSavePayload = (
    draft: IAgentDetails,
    data: IAgentDetails,
    scope: AgentDetailSaveScope = "all"
): Omit<AgentDetailSavePayload, "userId"> => {
    if (scope === "all") {
        const payload: Omit<AgentDetailSavePayload, "userId"> = {};
        if (!isEqual(draft.personalInformation, data.personalInformation)) {
            payload.personalInformation = draft.personalInformation;
        }
        if (!isEqual(draft.businessInformation, data.businessInformation)) {
            payload.businessInformation = draft.businessInformation;
        }
        if (
            !isEqual(
                draft.contractAndLicensingInformation,
                data.contractAndLicensingInformation
            )
        ) {
            payload.contractAndLicensingInformation = draft.contractAndLicensingInformation;
        }
        return payload;
    }

    if (isContractSubScope(scope)) {
        const { subField } = scope;
        const draftContract = draft.contractAndLicensingInformation;
        const dataContract = data.contractAndLicensingInformation;
        if (isEqual(draftContract[subField], dataContract[subField])) return {};
        return {
            contractAndLicensingInformation: {
                ...dataContract,
                [subField]: draftContract[subField],
            },
        };
    }

    if (!isEqual(draft[scope], data[scope])) {
        return { [scope]: draft[scope] };
    }
    return {};
};

export const isScopeDirty = (
    draft: IAgentDetails,
    data: IAgentDetails,
    scope: AgentDetailSaveScope
): boolean => Object.keys(buildAgentSavePayload(draft, data, scope)).length > 0;

export const applyResetScopeToDraft = (
    draft: IAgentDetails,
    data: IAgentDetails,
    scope: AgentDetailSaveScope
): IAgentDetails => {
    if (scope === "all") return cloneAgentDetails(data);

    if (isContractSubScope(scope)) {
        const { subField } = scope;
        return {
            ...cloneAgentDetails(draft),
            contractAndLicensingInformation: {
                ...draft.contractAndLicensingInformation,
                [subField]: structuredClone(data.contractAndLicensingInformation[subField]),
            },
        };
    }

    return {
        ...cloneAgentDetails(draft),
        [scope]: structuredClone(data[scope]),
    };
};
